/**
 * One place that knows how a tool's finished canvas becomes a downloadable file
 * in the format the user picked — so resize, watermark, background removal and
 * the rest all encode (and name) their output the same way.
 *
 * `original` keeps the format the picture arrived in (a GIF or BMP comes back as
 * PNG, a HEIC as JPG — the browser can write neither of those).
 */
export const OUTPUT_FORMAT_META = {
    jpg: { mime: 'image/jpeg', ext: 'jpg', alpha: false, lossy: true },
    png: { mime: 'image/png', ext: 'png', alpha: true, lossy: false },
    webp: { mime: 'image/webp', ext: 'webp', alpha: true, lossy: true },
    avif: { mime: 'image/avif', ext: 'avif', alpha: true, lossy: true },
};

/**
 * Formats that are a file, not a canvas encoding — there is no MIME type to ask
 * the browser about, so they are always on offer where a tool produces them.
 */
const NON_CANVAS_FORMATS = new Set(['pdf', 'svg']);

/**
 * What each kind of tool offers in its action-bar picker, in the order it offers
 * them. They live here because they are lists of the ids above; the picker
 * filters out whatever the browser cannot actually write.
 */
export const PAGE_OUTPUT_FORMATS = ['pdf', 'jpg', 'png', 'webp', 'avif'];
export const QR_OUTPUT_FORMATS = ['png', 'jpg', 'webp', 'avif', 'svg', 'pdf'];

const encodeSupport = new Map();

/** Can this browser WRITE the MIME type? (Chrome/Firefox: AVIF varies by version.) */
export function canEncodeMime(mime) {
    if (encodeSupport.has(mime)) return encodeSupport.get(mime);
    let ok = false;
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        ok = canvas.toDataURL(mime).startsWith(`data:${mime}`);
    } catch {
        ok = false;
    }
    encodeSupport.set(mime, ok);
    return ok;
}

/**
 * The subset of `ids` this browser can produce. `original`, PDF and SVG are
 * always offered, and so is AVIF: where the canvas cannot write it,
 * `encodeCanvas` does it in WebAssembly instead.
 */
export function availableOutputFormats(ids) {
    return ids.filter((id) => id === 'original'
        || id === 'avif'
        || NON_CANVAS_FORMATS.has(id)
        || (OUTPUT_FORMAT_META[id] && canEncodeMime(OUTPUT_FORMAT_META[id].mime)));
}

/**
 * AVIF through Squoosh's encoder (`@jsquash/avif`, WebAssembly) — Chrome's canvas
 * cannot write AVIF at all: `toBlob('image/avif')` quietly hands back a PNG.
 * Imported on first use, so only someone who picks AVIF downloads it.
 *
 * AVIF's quality scale is not JPEG's: around 50 already looks like JPEG 85, so
 * the 0…1 dial is mapped onto 0…70 rather than 0…100 (which only grows the file).
 * Speed 8 of 10 keeps a phone photo to a few seconds.
 */
async function encodeAvifWasm(canvas, quality) {
    const { default: encode } = await import('@jsquash/avif/encode.js');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const q = Math.round(Math.min(1, Math.max(0.05, quality)) * 70);
    const buffer = await encode(imageData, { quality: q, speed: 8 });
    return new Blob([buffer], { type: 'image/avif' });
}

/** The output format that "keep original" means for this file. */
export function sourceFormatOf(file) {
    const type = (file?.type || '').toLowerCase();
    const name = (file?.name || '').toLowerCase();
    if (type === 'image/png' || name.endsWith('.png')) return 'png';
    if (type === 'image/webp' || name.endsWith('.webp')) return 'webp';
    if (type === 'image/avif' || name.endsWith('.avif')) return 'avif';
    if (type === 'image/gif' || type === 'image/bmp' || /\.(gif|bmp)$/.test(name)) return 'png';
    return 'jpg';
}

export function resolveOutputFormat(format, file) {
    if (!format || format === 'original') return sourceFormatOf(file);
    return OUTPUT_FORMAT_META[format] ? format : 'png';
}

/**
 * `{ mime, ext, quality, alpha }` — the shape the resize and watermark engines
 * already encode through (their old `outputMeta(file)`), so picking a format is
 * a one-line change there instead of a rewrite.
 */
export function legacyMetaFor(format) {
    const meta = OUTPUT_FORMAT_META[format] || OUTPUT_FORMAT_META.png;
    return { mime: meta.mime, ext: meta.ext, quality: meta.lossy ? 0.92 : null, alpha: meta.alpha };
}

/** The extension that matches what a blob really is (not what was asked for). */
export function extForBlob(blob, fallback = 'png') {
    const hit = Object.values(OUTPUT_FORMAT_META).find((meta) => meta.mime === blob?.type);
    return hit?.ext || fallback;
}

export function formatNeedsBackground(format) {
    return OUTPUT_FORMAT_META[format]?.alpha === false;
}

function toBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), mime, quality);
    });
}

/**
 * Encode a finished canvas as `format`.
 *
 * JPG has no alpha, so the picture is laid onto `background` first — otherwise
 * every transparent pixel turns black. A browser that cannot write the format
 * quietly hands back a PNG from `toBlob`; that is caught by checking the blob's
 * type, and AVIF then falls back to WebP (same idea, broadly supported) rather
 * than shipping a PNG with an .avif name.
 *
 * @returns {Promise<{ blob: Blob, format: string, ext: string }>}
 */
export async function encodeCanvas(canvas, format, { quality = 0.92, background = '#ffffff' } = {}) {
    const meta = OUTPUT_FORMAT_META[format] || OUTPUT_FORMAT_META.png;
    let source = canvas;
    if (!meta.alpha) {
        source = document.createElement('canvas');
        source.width = canvas.width;
        source.height = canvas.height;
        const ctx = source.getContext('2d');
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, source.width, source.height);
        ctx.drawImage(canvas, 0, 0);
    }

    if (format === 'avif' && !canEncodeMime(meta.mime)) {
        try {
            return { blob: await encodeAvifWasm(source, quality), format, ext: meta.ext };
        } catch {
            // The WebAssembly module failed to load (offline, blocked) — a WebP
            // named .webp beats no file at all.
            return encodeCanvas(canvas, 'webp', { quality, background });
        }
    }

    const blob = await toBlob(source, meta.mime, meta.lossy ? quality : undefined);
    if (blob.type === meta.mime) return { blob, format, ext: meta.ext };

    if (format === 'avif') return encodeCanvas(canvas, 'webp', { quality, background });
    const actual = Object.entries(OUTPUT_FORMAT_META).find(([, m]) => m.mime === blob.type);
    return { blob, format: actual?.[0] || 'png', ext: actual?.[1].ext || 'png' };
}

/** `photo.heic` + `webp` → `photo.webp`; an optional suffix goes before the dot. */
export function outputNameFor(originalName, ext, suffix = '') {
    const base = String(originalName || 'image').replace(/\.[^.]+$/, '');
    return `${base}${suffix}.${ext}`;
}
